import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Eye, EyeOff, Lock, Unlock,
  ChevronUp, ChevronDown, ChevronRight,
  Type, Image as ImageIcon, Square, Scissors, Link,
  Folder, FolderOpen,
} from 'lucide-react';
import { buildLayerTree, getEffectiveState } from '../../lib/groups/groupUtils';

const getElementIcon = (element) => {
  switch (element.type) {
    case 'text':
      return <Type className="w-4 h-4" />;
    case 'image':
      return <ImageIcon className="w-4 h-4" />;
    case 'shape':
      return <Square className="w-4 h-4" />;
    default:
      return <Square className="w-4 h-4" />;
  }
};

const defaultNameForElement = (el) => {
  if (el.type === 'group') return 'Group';
  if (el.type === 'text') return el.text ? `Text — ${String(el.text).slice(0, 24)}` : 'Text Layer';
  if (el.type === 'image') return 'Image';
  if (el.type === 'shape') {
    const s = el.shape;
    return typeof s === 'string' && s.length > 0
      ? s[0].toUpperCase() + s.slice(1)
      : 'Shape';
  }
  if (el.type === 'svg') return 'SVG';
  if (el.type === 'qrcode') return 'QR Code';
  if (el.type === 'penPath') return 'Pen Path';
  if (el.type === 'chart') return 'Chart';
  return el.type || 'Element';
};

/**
 * Hierarchical Layers panel.
 *
 * Renders the page's elements as a recursive tree via `buildLayerTree`:
 *   - Group rows have an expand/collapse chevron + folder icon + child count.
 *   - Children are rendered indented under their group (12px per depth level).
 *   - Top-level entries are reversed so top-of-stack appears first (matches
 *     editor convention where layer panel top = canvas top).
 *
 * State-sync surfaces (per scene-graph requirements):
 *   - `selectedElement` and `selectedElements` BOTH contribute to row "selected"
 *     styling. Either single-selection match OR multi-selection membership
 *     highlights the row.
 *   - Click handler passes `e.shiftKey` to `onSelectElement` so multi-select
 *     from the panel works identically to canvas multi-select.
 *   - Click on a locked row is blocked at the row level (existing convention).
 *     Locking icon itself remains interactive via stopPropagation so users can
 *     unlock a locked parent group.
 *   - Visibility / locked icons display the CASCADED (effective) state via
 *     `getEffectiveState`. Toggle actions write the element's OWN field; the
 *     cascade is read-only. So locking a group displays its children as
 *     locked, but each child retains its own locked field at the data layer.
 *
 * Expand/collapse persistence: state is local to the panel (not persisted
 * across remounts). Default = expanded for any group not in the state map.
 *
 * Move up/down buttons: preserved for leaf rows only. Group reorder via the
 * panel is out of scope here — the existing onMoveLayer pipes through to the
 * flat array's move-by-id handler which doesn't understand hierarchy. Future
 * polish task can add hierarchy-aware reorder.
 *
 * Orphans (elements whose parentId points to a missing group) are promoted
 * to top-level by `buildLayerTree` — defensive against corrupt data.
 */
const LayersPanel = ({
  elements,
  selectedElement,
  selectedElements = [],
  onSelectElement,
  onUpdateElement,
  onMoveLayer,
  onReorderLayer,
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const isExpanded = (groupId) => expandedGroups[groupId] !== false;
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !isExpanded(groupId) }));
  };

  // Inline rename: tracks which element is currently being renamed and the
  // working draft string. Mutually exclusive — only one row can be in
  // rename mode at a time. Enter/blur commit, Escape discards.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  // Drag-reorder state. `draggingId` is set on dragstart; `dragOver` tracks
  // the currently-hovered drop target + position ('above' | 'below'). The
  // insertion indicator is rendered as a 2px top/bottom border on the
  // hovered row so the user sees exactly where the drop will land —
  // matches Figma/Canva behaviour. Cleared on dragend/drop.
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { targetId, position }

  const handleDragStart = (el, e) => {
    if (renamingId === el.id) return; // don't start drag while renaming
    setDraggingId(el.id);
    // dataTransfer is required for Firefox to fire dragover/drop; the
    // payload itself isn't read (we use draggingId from state).
    try {
      e.dataTransfer.setData('text/plain', el.id);
      e.dataTransfer.effectAllowed = 'move';
    } catch {}
  };

  const handleDragOverRow = (el, e) => {
    if (!draggingId || draggingId === el.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const position = (e.clientY - rect.top) < rect.height / 2 ? 'above' : 'below';
    setDragOver((prev) =>
      prev && prev.targetId === el.id && prev.position === position
        ? prev
        : { targetId: el.id, position }
    );
  };

  const handleDropOnRow = (el, e) => {
    e.preventDefault();
    if (draggingId && draggingId !== el.id && dragOver) {
      onReorderLayer?.(draggingId, el.id, dragOver.position);
    }
    setDraggingId(null);
    setDragOver(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOver(null);
  };

  const startRename = (el, e) => {
    e.stopPropagation();
    setRenamingId(el.id);
    setRenameDraft(el.name || defaultNameForElement(el));
  };

  const commitRename = () => {
    if (!renamingId) return;
    const trimmed = renameDraft.trim();
    // Empty input falls back to undefined so the row reverts to the
    // type-derived default name rather than persisting an empty string.
    onUpdateElement(renamingId, { name: trimmed || undefined });
    setRenamingId(null);
    setRenameDraft('');
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };

  const handleToggleVisibility = (element, e) => {
    e.stopPropagation();
    onUpdateElement(element.id, { visible: element.visible === false });
  };

  const handleToggleLock = (element, e) => {
    e.stopPropagation();
    onUpdateElement(element.id, { locked: !element.locked });
  };

  const handleMoveUp = (element, e) => {
    e.stopPropagation();
    onMoveLayer?.(element.id, 'up');
  };

  const handleMoveDown = (element, e) => {
    e.stopPropagation();
    onMoveLayer?.(element.id, 'down');
  };

  const renderRow = (node, depth) => {
    const el = node.element;
    // Cascaded visible/locked state (AND-visible / OR-locked across ancestors).
    // Read-only for display; toggles still mutate the element's OWN field.
    const effective = getEffectiveState(elements, el.id);
    const isSel =
      selectedElement?.id === el.id ||
      (selectedElements || []).some((s) => s.id === el.id);
    const isGroup = el.type === 'group';
    const isMaskShape = el.isMaskShape || el.isClipBase;
    const isContent = !!el.clippedTo;
    const childCount = isGroup ? node.children.length : 0;
    const expanded = isGroup && isExpanded(el.id);
    const indent = depth * 12;

    const isDropTarget = dragOver && dragOver.targetId === el.id;
    const isBeingDragged = draggingId === el.id;
    // Indicator: 2px purple line at top or bottom of the row matching where
    // the dragged source will land. boxShadow (not border) so it doesn't
    // shift layout / replace the selection border colour.
    const dropIndicatorStyle = isDropTarget
      ? dragOver.position === 'above'
        ? { boxShadow: 'inset 0 2px 0 0 #8b5cf6' }
        : { boxShadow: 'inset 0 -2px 0 0 #8b5cf6' }
      : {};

    return (
      <React.Fragment key={el.id}>
        <div
          draggable={renamingId !== el.id}
          onDragStart={(e) => handleDragStart(el, e)}
          onDragOver={(e) => handleDragOverRow(el, e)}
          onDrop={(e) => handleDropOnRow(el, e)}
          onDragEnd={handleDragEnd}
          onClick={(e) => {
            if (effective.locked) return; // locked rows ignore body clicks
            onSelectElement?.(el, e.shiftKey);
          }}
          data-layer-id={el.id}
          data-layer-type={el.type}
          data-layer-depth={depth}
          data-layer-selected={isSel ? '1' : '0'}
          className={`group p-2 rounded border cursor-pointer transition-all ${
            isSel
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
          } ${!effective.visible ? 'opacity-50' : ''} ${effective.locked ? 'italic' : ''} ${isBeingDragged ? 'opacity-40' : ''}`}
          style={{ marginLeft: indent, ...dropIndicatorStyle }}
        >
          <div className="flex items-center gap-2">
            {isGroup ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(el.id);
                }}
                className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-800 flex-shrink-0"
                aria-label={expanded ? 'Collapse group' : 'Expand group'}
                data-testid={`toggle-group-${el.id}`}
              >
                {expanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            ) : (
              <span className="w-4 inline-block flex-shrink-0" />
            )}
            {isMaskShape && (
              <Scissors
                className="w-3 h-3 text-purple-500 flex-shrink-0"
                aria-label="Mask shape"
              >
                <title>
                  {Array.isArray(el.maskChildIds) && el.maskChildIds.length > 0
                    ? `Mask shape — ${el.maskChildIds.length} clipped`
                    : 'Mask shape'}
                </title>
              </Scissors>
            )}
            {isContent && (
              <Link
                className="w-3 h-3 text-indigo-400 flex-shrink-0"
                aria-label="Clipped layer"
              >
                <title>Clipped layer</title>
              </Link>
            )}
            <div className="flex-shrink-0">
              {isGroup
                ? expanded
                  ? <FolderOpen className="w-4 h-4" />
                  : <Folder className="w-4 h-4" />
                : getElementIcon(el)}
            </div>
            <div className="flex-1 min-w-0">
              {renamingId === el.id ? (
                <input
                  type="text"
                  value={renameDraft}
                  autoFocus
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitRename();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelRename();
                    }
                    // Stop bubble so editor-wide shortcuts (Delete, Cmd+G, etc.)
                    // don't fire while typing in the rename input.
                    e.stopPropagation();
                  }}
                  onBlur={commitRename}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-full text-sm font-medium bg-white border border-purple-400 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-purple-400"
                  data-testid={`rename-input-${el.id}`}
                />
              ) : (
                <p
                  className="text-sm font-medium truncate cursor-text"
                  onDoubleClick={(e) => startRename(el, e)}
                  title="Double-click to rename"
                >
                  {el.name || defaultNameForElement(el)}
                </p>
              )}
              {isGroup && (
                <p className="text-xs text-gray-500">
                  {childCount} {childCount === 1 ? 'layer' : 'layers'}
                </p>
              )}
              {/* Mask shape / clipped layer captions moved into the role
                  icon's tooltip — keeps rows single-line and matches Figma's
                  compact layer hierarchy. Icons + colour-coding still
                  communicate the role at a glance. */}
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onMoveLayer && !isGroup && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={(e) => handleMoveUp(el, e)}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={(e) => handleMoveDown(el, e)}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={(e) => handleToggleVisibility(el, e)}
                title={effective.visible ? 'Hide' : 'Show'}
                data-testid={`toggle-visibility-${el.id}`}
              >
                {effective.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={(e) => handleToggleLock(el, e)}
                title={effective.locked ? 'Unlock' : 'Lock'}
                data-testid={`toggle-lock-${el.id}`}
              >
                {effective.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
              </Button>
            </div>
          </div>
        </div>
        {isGroup && expanded &&
          node.children.slice().reverse().map((child) => renderRow(child, depth + 1))
        }
      </React.Fragment>
    );
  };

  const tree = buildLayerTree(elements);

  // Embed-friendly render: no outer width / border chrome so the caller
  // controls layout (e.g. dropped into RightSidebar's Layers tab content).
  // Empty state is rendered by the caller if it wants chrome around it;
  // here we return null so empty layers don't take any space.
  if (!elements || elements.length === 0) return null;

  return (
    <div className="space-y-1">
      {tree.slice().reverse().map((node) => renderRow(node, 0))}
    </div>
  );
};

export default LayersPanel;
